import {createContext, useCallback, useEffect, useState} from "react";
import axiosInstance from "./AxiosInstance";
import i18n from "./i18n";

const MyContext = createContext("");

export function ContextProvider({children}) {
    const [dashboards, setDashboards] = useState([]);
    const [user, setUser] = useState({});
    const [userLoaded, setUserLoaded] = useState(false);
    const [companyChange, setCompanyChange] = useState(0);
    const [corporateGroup, setCorporateGroup] = useState({});
    const [systemSettings, setSystemSettings] = useState({sms_enabled: false, whatsapp_enabled: false});

    const isSuperuserImpersonating = !!localStorage.getItem('superuser_access_token');
    const impersonatingName = localStorage.getItem('impersonating_name') || '';

    const stopImpersonating = () => {
        localStorage.setItem('access_token', localStorage.getItem('superuser_access_token'));
        localStorage.setItem('refresh_token', localStorage.getItem('superuser_refresh_token'));
        const savedCompany = localStorage.getItem('superuser_company_id');
        const savedProject = localStorage.getItem('superuser_project_id');
        if (savedCompany) localStorage.setItem('company_id', savedCompany);
        else localStorage.removeItem('company_id');
        if (savedProject) localStorage.setItem('project_id', savedProject);
        else localStorage.removeItem('project_id');
        localStorage.removeItem('superuser_access_token');
        localStorage.removeItem('superuser_refresh_token');
        localStorage.removeItem('superuser_company_id');
        localStorage.removeItem('superuser_project_id');
        localStorage.removeItem('superuser_dashboard_id');
        localStorage.removeItem('impersonating_name');
        localStorage.removeItem('offices');
        localStorage.removeItem('project');
        window.location.href = '/admin';
    };

    const refreshCorporateGroup = useCallback(() => {
        let authenticated = localStorage.getItem('access_token') !== null;
        if(authenticated) {
            axiosInstance.get('corporate_group/get_current/').then((res) => {
                setCorporateGroup(res.data);
            });
        }
    }, []);

    useEffect(() => {
        const company_id = parseInt(localStorage.getItem('company_id'));
        let query = '';
        if(company_id) {
            query = `?company_id=${company_id}`;
        }
        let authenticated = localStorage.getItem('access_token') !== null;
        if(authenticated) {
            axiosInstance.get(`dashboard/${query}`).then((res) => {
                setDashboards(res.data);
            });

            axiosInstance.get('corporate_group/get_current/').then((res) => {
                setCorporateGroup(res.data);
            });

            axiosInstance.get('current_user/').then((response) => {
                setUser(response.data);
                setUserLoaded(true);
                if (response.data?.system_settings) {
                    setSystemSettings(response.data.system_settings);
                }

                // Sync user language preference
                const lang = response.data?.language || 'en';
                localStorage.setItem('user_language', lang);
                if (i18n.language !== lang) {
                    i18n.changeLanguage(lang);
                }

                // Bootstrap company/project/offices for users who bypass the hub
                // (e.g. users without dashboards permission who skip the company selector)
                const { allowed_companies, allowed_projects } = response.data;
                const hasProjectSet = !!localStorage.getItem('project_id');

                // Always refresh offices from fresh allowed_projects (fixes stale localStorage after admin edits)
                if (allowed_projects && allowed_projects.length > 0) {
                    const currentCompanyId = localStorage.getItem('company_id');
                    if (currentCompanyId) {
                        const officesForCompany = allowed_projects.filter(
                            p => String(p.company_id) === currentCompanyId
                        );
                        if (officesForCompany.length > 0) {
                            localStorage.setItem('offices', JSON.stringify(officesForCompany));
                            // If current project was removed from allowed list, switch to first available
                            const currentProjectId = localStorage.getItem('project_id');
                            if (currentProjectId && !officesForCompany.find(p => String(p.id) === currentProjectId)) {
                                localStorage.setItem('project', JSON.stringify(officesForCompany[0]));
                                localStorage.setItem('project_id', String(officesForCompany[0].id));
                                window.location.reload();
                            }
                        }
                    }
                }

                if (!hasProjectSet) {
                    const multiCompany = allowed_companies && allowed_companies.length > 1;
                    if (allowed_projects && allowed_projects.length > 0 && !multiCompany) {
                        // Single-company restricted user: auto-bootstrap from their allowed list
                        const firstProject = allowed_projects[0];
                        localStorage.setItem('project', JSON.stringify(firstProject));
                        localStorage.setItem('project_id', String(firstProject.id));
                        localStorage.setItem('offices', JSON.stringify(allowed_projects));
                        if (allowed_companies && allowed_companies.length === 1) {
                            localStorage.setItem('company_id', String(allowed_companies[0].id));
                        }
                        window.location.reload();
                    } else if (!localStorage.getItem('company_id')) {
                        // Full-access user bypassing hub: try company API (handles non-corporate plan=0)
                        axiosInstance.get('company/').then((companyRes) => {
                            if (companyRes.status === 206) {
                                const company = companyRes.data;
                                localStorage.setItem('company_id', String(company.id));
                                const defaultProject = company.default_project || (company.projects?.length > 0 ? company.projects[0] : null);
                                if (defaultProject) {
                                    localStorage.setItem('project', JSON.stringify(defaultProject));
                                    localStorage.setItem('project_id', String(defaultProject.id));
                                    localStorage.setItem('offices', JSON.stringify(company.projects || []));
                                    window.location.reload();
                                }
                            }
                            // For corporate mode, hub.js handles company selection
                        }).catch(() => {});
                    }
                }
            })
        }
    }, []);

    useEffect(() => {
        let authenticated = localStorage.getItem('access_token') !== null;
        if(authenticated) {
            setDashboards([]);
            const company_id = parseInt(localStorage.getItem('company_id'));

            if(!isNaN(company_id) && company_id) {
                axiosInstance.get(`dashboard/?company_id=${company_id}`).then((res) => {
                    setDashboards(res.data);
                });
            }
        }
    }, [companyChange]);

    return(
        <MyContext.Provider value={{dashboards, setCompanyChange, user, userLoaded, corporateGroup, refreshCorporateGroup, isSuperuserImpersonating, stopImpersonating, systemSettings, setSystemSettings}}>
            {isSuperuserImpersonating && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 99999,
                    backgroundColor: '#DC2626',
                    color: 'white',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                    <span>Viewing as <strong>{impersonatingName}</strong> — Changes will affect this client's account</span>
                    <button
                        onClick={stopImpersonating}
                        style={{
                            background: 'white',
                            color: '#DC2626',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 14px',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: '12px'
                        }}
                    >
                        Return to Admin
                    </button>
                </div>
            )}
            {children}
        </MyContext.Provider>
    )
}
export default MyContext;
